import React, { useEffect, useRef, useState } from "react";
import { FolderLock, Upload, Trash2, FileText, Loader2, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";
import { api, fileToBase64, base64ToBlob, triggerDownload } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

const ALLOWED = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

const Vault = () => {
  const { t } = useT();
  const DOC_TYPES = {
    loonstrook: t("p2.dt.loonstrook"), id: t("p2.dt.id"), werkgever: t("p2.dt.werkgever"),
    uittreksel: t("p2.dt.uittreksel"), huurverklaring: t("p2.dt.huurverklaring"), overig: t("p2.dt.overig"),
  };
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("loonstrook");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [tone, setTone] = useState("uitgebreid");
  const [gening, setGening] = useState(false);
  const fileRef = useRef(null);

  const load = () => api("/vault").then(setData).catch(() => setData({ documents: [], max_docs: 0 }));
  useEffect(() => { load(); }, []);

  const onFile = (f) => {
    if (!f) return;
    if (!ALLOWED.includes(f.type)) return toast.error(t("p2.vault.errType"));
    if (f.size > 8 * 1024 * 1024) return toast.error(t("p2.vault.errSize"));
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
  };

  const upload = async () => {
    if (!file) return toast.error(t("p2.vault.errPick"));
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      await api("/vault", { method: "POST", body: JSON.stringify({ name: name || file.name, doc_type: docType, mime: file.type, data: b64 }) });
      setOpen(false); setFile(null); setName(""); setDocType("loonstrook");
      load(); toast.success(t("p2.vault.added"));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const remove = async (id) => { await api(`/vault/${id}`, { method: "DELETE" }); load(); };

  const generate = async () => {
    setGening(true);
    try {
      const res = await api("/vault/generate-package", { method: "POST", body: JSON.stringify({ tone }) });
      triggerDownload(base64ToBlob(res.pdf_base64, "application/pdf"), res.filename || "huurdossier.pdf");
      setGenOpen(false);
      toast.success(t("p2.vault.generated"));
    } catch (e) { toast.error(e.message); } finally { setGening(false); }
  };

  const docs = data?.documents || [];

  return (
    <div>
      <PageHeader
        icon={FolderLock}
        title={t("p2.vault.title")}
        subtitle={t("p2.vault.subtitle")}
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-white text-navy hover:bg-white/90" data-testid="vault-upload-btn">
                <Upload size={16} className="mr-2" /> {t("p2.vault.upload")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">{t("p2.vault.uploadTitle")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t("p2.vault.docType")}</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(DOC_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("p2.vault.name")}</Label>
                  <Input className="mt-2 rounded-xl" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div>
                  <input ref={fileRef} type="file" accept={ALLOWED.join(",")} className="hidden" data-testid="vault-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
                  <button onClick={() => fileRef.current?.click()} className="flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-border bg-slate-50 p-6 text-slate-500 hover:border-brandblue">
                    <Upload size={22} />
                    <span className="text-sm">{file ? file.name : t("p2.vault.pick")}</span>
                  </button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={upload} disabled={busy} data-testid="vault-save" className="rounded-xl bg-brandblue hover:bg-brandblue/90">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : t("p2.vault.add")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-border bg-brandblue-light/40 p-6">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brandblue text-white"><Sparkles size={20} /></span>
            <div>
              <div className="font-display font-bold text-navy">{t("p2.vault.genTitle")}</div>
              <div className="text-sm text-slate-500">{t("p2.vault.genDesc")}</div>
            </div>
          </div>
          <Dialog open={genOpen} onOpenChange={setGenOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-brandblue hover:bg-brandblue/90" data-testid="vault-generate-btn">
                <Download size={16} className="mr-2" /> {t("p2.vault.genBtn")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">{t("p2.vault.genModal")}</DialogTitle></DialogHeader>
              {!user?.is_premium && (
                <p className="rounded-xl bg-warning/10 px-4 py-3 text-sm text-warning">{t("p2.vault.premiumNote")}</p>
              )}
              <div>
                <Label>{t("p2.vault.tone")}</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="uitgebreid">{t("p2.vault.toneLong")}</SelectItem>
                    <SelectItem value="kort">{t("p2.vault.toneShort")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button onClick={generate} disabled={gening} data-testid="vault-generate-confirm" className="rounded-xl bg-brandblue hover:bg-brandblue/90">
                  {gening ? <><Loader2 className="mr-2 animate-spin" size={18} /> {t("p2.vault.generating")}</> : t("p2.vault.genConfirm")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {data === null ? (
          <div className="grid gap-3 sm:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-slate-100" />)}</div>
        ) : docs.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-16 text-center" data-testid="vault-empty">
            <FolderLock className="mx-auto text-slate-300" size={44} />
            <p className="mt-4 text-slate-500">{t("p2.vault.empty")}</p>
          </div>
        ) : (
          <>
            <p className="mb-3 text-sm text-slate-500">{t("p2.vault.count", { n: docs.length, max: data.max_docs })}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {docs.map((d) => (
                <div key={d.id} data-testid={`vault-doc-${d.id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-white p-4 shadow-sm">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brandblue-light text-brandblue"><FileText size={20} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-navy">{d.name}</div>
                    <div className="text-xs text-slate-400">{d.type_label} · {d.size_kb} KB</div>
                  </div>
                  <Button variant="outline" size="icon" className="rounded-full text-danger" onClick={() => remove(d.id)} data-testid={`vault-delete-${d.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Vault;
